import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "helpers/utils";
import { LoaderCircle, type LucideIcon } from "lucide-react";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-900 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-solid border-transparent",
  {
    variants: {
      variant: {
        default: "bg-primary-900 text-white hover:bg-primary-800 shadow-sm",
        destructive: "bg-system-red text-white hover:bg-red-600 shadow-sm",
        outline:
          "border-gray-200 bg-white text-primary-900 hover:bg-gray-100 hover:text-gray-900 shadow-sm",
        "light-green":
          "bg-sub-menu-green text-primary-900 hover:bg-sub-menu-green/70 font-bold shadow-none border-transparent rounded-full hover:no-underline",
        secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200",
        ghost: "hover:bg-gray-100 hover:text-gray-900",
        link: "text-primary-900 underline-offset-4 hover:underline",
        pill: "rounded-full bg-sub-menu-green text-dark-teal hover:bg-sub-menu-green/70 shadow-sm",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  icon?: LucideIcon;
  iconClassName?: string;
}

export interface IconButtonProps extends ButtonProps {
  icon?: LucideIcon;
  iconPosition?: "left" | "right";
  iconClassName?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      icon: Icon,
      iconClassName,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const ResolvedIcon = isLoading ? LoaderCircle : Icon;

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {ResolvedIcon && (
          <ResolvedIcon
            className={cn(
              "h-4 w-4 shrink-0 mr-2",
              isLoading && "animate-spin",
              iconClassName
            )}
          />
        )}
        {children}
      </Comp>
    );
  }
);
Button.displayName = "Button";

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      variant,
      size,
      icon: Icon,
      iconPosition = "right",
      iconClassName,
      isLoading = false,
      children,
      ...props
    },
    ref
  ) => {
    const ResolvedIcon = isLoading ? LoaderCircle : Icon;

    return (
      <Button
        className={cn(
          "gap-2 relative pr-10 h-9 group border border-transparent hover:border-dark-teal/60",
          className
        )}
        variant={variant}
        size={size}
        isLoading={false}
        disabled={props.disabled || isLoading}
        ref={ref}
        {...props}
      >
        {iconPosition === "left" && ResolvedIcon && (
          <ResolvedIcon
            className={cn(
              "h-4 w-4",
              isLoading && "animate-spin",
              iconClassName
            )}
          />
        )}
        {children}
        {iconPosition === "right" && ResolvedIcon && (
          <span className="shrink-0 size-7 flex items-center justify-center rounded-full bg-dark-teal text-white group-hover:text-accent absolute right-1 transition-colors">
            <ResolvedIcon
              className={cn(
                "h-4 w-4",
                isLoading && "animate-spin",
                iconClassName
              )}
            />
          </span>
        )}
      </Button>
    );
  }
);
IconButton.displayName = "IconButton";

export { Button, IconButton, buttonVariants };
